/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2020, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'react',
	'mobx-react',
	'@ba/ba-ui-carbon-toolkit',
	'@ba/ba-ui-carbon',
	'prop-types'
], function(
	React,
	MobxReact,
	Toolkit,
	BA_UI_CARBON,
	PropTypes
) {

	const { FlexLayout, FlexItem, HSpacer, Label, SVGIcon } = Toolkit;
	const { Button, Link } = BA_UI_CARBON;

	/**
	 * @class SingleLineLinks
	 * @description SingleLineLinks is a property layout control class of type container. It contains 2 or 3 children properties 'left', ['middle'] and 'right'
	 * and will render these 2 or 3 properties on the same row. One of the children properties can be a link to other property or component.  
	 * 
	 * @example Example props usage to render a SingleLineLinks item:
	 * {
	 *		item: // see example of SingleLineLinks item with 3 children
	 *		className: 'classNameForThisSingleLineLinks',
	 *		makeSectionItemCb: f() // callback function to generate JSX for each property child
	 *		getIconCb: f() // callback function to get icon id,
	 *		logger: object // to log errors/warnings
	 * }
	 * 
	 *  Example of SingleLineLinks item with 3 children:
	 * {
	 *	'id': 'fontFamilyLine',
	 *	'type': 'SingleLineLinks',
	 *	'items': [{
	 *		'id': 'fontFamily',
	 *		'align': 'left',
	 *		'items': [{
	 *			'type': 'DropDown',
	 *			'ariaDescribedby': 'Font family',
	 * 			...
	 *		}]
	 *	}, {
	 *		'id': 'fontSize',
	 *		'align': 'middle',
	 *		'items': [{
	 *			'type': 'DropDown',
	 *			'label': 'Font size',
	 *			'ariaDescribedby': 'Font size',
	 *			...
	 *		}]
	 *	}, {
	 *		'id': 'textColor',
	 *		'align': 'right',
	 *		'items': [{
	 *			'paletteType': 'DashboardColorSet',
	 *			'type': 'ColorPicker',
	 *			'showHexValue': true,
	 *			'addButton': true,
	 *			...
	 *		}]
	 *	}]
	 * }
	 * 
	 * Another example of SingleLineLinks item with icon and text (from Tabs-Individual ):
	 * sampleSingleLineLinks = {
	 *	id: 'singleLineLinks',
	 *	editor: {
	 *		sectionId: 'sectionX',
	 *		uiControl: {
	 *			type: 'SingleLineLinks',
	 *			clickCallback: clickCallback,
	 *			items: [{
	 *				align: 'left',
	 *				items: [{
	 *					type: 'icon',
	 *					svgIcon: tabIcon !== 'transparent' ? tabIcon : null,
	 *					iconTooltip: StringResources.get('propIndividualTabStylesAriaLabel')
	 *				}]
	 *			}, {
	 *				align: 'right',
	 *				items: [{
	 *					type: 'text',
	 *					value: page.getPropertyValue('title'),
	 *					showRightArrow: true
	 *				}]
	 *			}]
	 *		}
	 *	}
	 * };
	 */
	class SingleLineLinks extends React.Component {

		render() {
			const { item, className, logger } = this.props;
			let layoutSpec = item;
			if (item && item.editor && item.editor.uiControl) {
				layoutSpec = item.editor.uiControl;
			}
			const { justifyContent, alignItems } = layoutSpec;
			const sectionItems = this._renderSectionItems(layoutSpec);
			if (!sectionItems.length) {
				logger.warn(`layout '${item.id ? item.id : 'undefined'}' is of type SingleLineLinks but does not have children`);
				return null;
			}
			const containerProps = {
				className,
				'data-property-id': (item && item.id) ? item.id : undefined
			};
			return (
				<FlexLayout
					{...containerProps}
					direction='row'
					wrap='nowrap'
					justifyContent={ justifyContent ? justifyContent : 'space-between'}
					alignItems={ alignItems || 'baseline'}
					onClick={(evt) => this._onClickItem(evt, layoutSpec)}
				>
					{sectionItems}
				</FlexLayout>
			);
		}

		_renderSectionItems(layoutItem) {
			let sectionItems = [];
			if (layoutItem && layoutItem.items && layoutItem.items.length) {
				const items = layoutItem.items;

				const left = items.find(singleItem => singleItem.align === 'left');
				let key = 0;
				if (left && left.items && left.items.length) {
					this._addSectionItem(left.items[0], 'left', key++, sectionItems);
				}
				let middle;
				if (items.length === 3) {
					middle = items.find(singleItem => singleItem.align === 'middle');
					if (middle && middle.items && middle.items.length) {
						this._addSectionItem(middle.items[0], 'middle', key++, sectionItems);
					}
				}
				const right = items.find(singleItem => singleItem.align === 'right');
				if (right && right.items && right.items.length) {
					right.items.forEach( item => this._addSectionItem(item, 'right', key, sectionItems, true /*lastItem*/) ); 
				}
			}
			return sectionItems;
		}


		_addSectionItem(item, alignment, key, sectionItems, lastItem=false) {
			let sectionItem;
			if (item.type === 'text' || item.type === 'textLink') {
				sectionItem = this._renderText(item, alignment);
			} else if (item.type === 'icon') {
				sectionItem = this._renderIcon(item, alignment);
			} else {
				// if the child is another layout type, use the factory to generate the item 
				sectionItem = this.props.makeSectionItemCb(item, key);
			}
			if (sectionItem) {
				sectionItems.push(sectionItem);
				if (!lastItem) {
					// a space between items in the row
					sectionItems.push(<HSpacer size={1}/>);
				}
			}
		}

		_renderText(item, alignment) {
			// If the text has option showRightArrow, render the icon  ">" on the right side
			let rightArrowIcon;
			if (item.showRightArrow) {
				rightArrowIcon = this._renderRightArrowIcon(item);
			}
			let content = null;
			if (item.type === 'textLink') {
				const classNameTextLink = `properties--controls--SingleLineLinks--${alignment}--textLink` + (item.id ? ` property--${item.id}` : '');
				content = <Link className= {classNameTextLink} onClick={(evt) => this._onClickItem(evt, item)}>{item.value}</Link>;
			} else if (item.type === 'text') {
				const classNameText = `properties--controls--SingleLineLinks--${alignment}--text` + (item.id ? ` property--${item.id} ` : '') + (item.class ? ' '+ item.class : '');
				content = <Label className={classNameText} label={item.value} onClick={(evt) => this._onClickItem(evt, item)} />;
			}
			return (
				<FlexItem className = "properties--controls--SingleLineLinks__textWithIcon" grow={item.grow === undefined ? true : item.grow}>
					{content}
					{rightArrowIcon}
				</FlexItem>

			);
		}

		_renderIcon(item, alignment) {
			if (!item.svgIcon) {
				return null;
			}
			const classNameIcon = `properties--controls--SingleLineLinks--${alignment}--icon` + (item.id ? ` property--${item.id} ` : '') + (item.class ? ' ' + item.class : '');
			let icon;
			const iconComponent = this.props.supportedIcons[item.svgIcon];
			if (iconComponent) {
				icon = (
					<Button
						kind="ghost"
						className={classNameIcon}
						iconDescription={item.iconTooltip}
						hasIconOnly
						renderIcon={iconComponent}
						tooltipPosition={item.tooltipPosition}
						onClick={(evt) => this._onClickItem(evt,item)}
					/>
				);
			} else {
				icon = (
					<SVGIcon
						iconId={item.svgIcon}
						className={classNameIcon}
						alt={item.iconTooltip}
						onClick={(evt) => this._onClickItem(evt,item)}
					>
					</SVGIcon>
				);
			}

			return (
				<FlexItem>
					{icon}
				</FlexItem>
			);
		}

		_onClickItem(evt,item) {
			if (item.clickCallback) {
				item.clickCallback(evt);
			}
		}

		_renderRightArrowIcon(item) {
			const iconId = this.props.getIconCb('common-chevron_right_16').id;
			return (
				<FlexItem>
					<SVGIcon
						iconId={iconId}
						className='svgIcon'
						onClick={(evt) => this._onClickItem(evt, item)}
					>
					</SVGIcon>
				</FlexItem>
			);
		}
	}

	SingleLineLinks.propTypes = {
		item: PropTypes.shape({
			id: PropTypes.string,
			type: 'SingleLineLinks',
			items: PropTypes.array.isRequired,
			editor: PropTypes.shape({
				uiControl: PropTypes.object
			})
		}),
		className: PropTypes.string,
		makeSectionItemCb: PropTypes.func.isRequired,
		getIconCb: PropTypes.func.isRequired,
		logger: PropTypes.object,
		supportedIcons: PropTypes.object
	};

	SingleLineLinks.defaultProps = {
		supportedIcons: {}
	};

	return MobxReact.observer(SingleLineLinks);
});
